/**
 * Own-write reconciliation — telling a version conflict with the editor's own
 * earlier save apart from a real one.
 *
 * A save can land on the server without the editor hearing back: the request
 * hit its client deadline, the connection dropped, or API Gateway answered 504
 * while the Lambda went on to commit. The editor treats it as failed and
 * retries against the version it still holds, so the server refuses the retry
 * as a version conflict — against the writer's own text. Shown as a conflict,
 * that asked the writer to choose between their own text and their own text.
 *
 * So every save is noted before it is sent, and the notes for a section are
 * dropped once a save for it succeeds: nothing sent earlier can land after
 * that, because it carries a version the success replaced. A 409 is our own
 * write when the server's current text is exactly one of the noted texts, at a
 * version past the one we sent. The save is then re-sent once against that
 * version, so the newest text goes on top and nothing of anyone else's is
 * overwritten (the server holds only what this editor already sent).
 *
 * Everything else is a genuine conflict and reaches the dialog as before:
 * different text — another person, another tab, or the AI — or a 409 at the
 * version we sent, which means the section is generating or locked. The
 * server's `lastEditedBy` cannot help: it names this user for their own other
 * tabs and for AI writes made on their behalf, which must still show.
 */

import { ApiRequestError, type SaveSegmentResponse, type SegmentConflictDetails } from '@/lib/api'
import { stripDiffMarkup } from '@/lib/utils'

/**
 * How many unconfirmed texts are kept per section. Auto-retries stop after
 * three, so this covers a stall, its retries and a manual Retry; an older text
 * the server still holds just shows the dialog, as it did before.
 */
const MAX_UNCONFIRMED_PER_SECTION = 5

export interface UnconfirmedSaveLog {
  /** Note a save about to be sent, before its outcome is known. */
  note(sectionId: string, content: string): void
  /** A save for this section succeeded: nothing sent before it can still land. */
  confirm(sectionId: string): void
  /**
   * The version to re-send against when `error` is a 409 showing text this
   * editor sent for the section and never saw confirmed; null otherwise.
   */
  ownWriteVersion(sectionId: string, error: unknown, sentVersion: number): number | null
}

export function createUnconfirmedSaveLog(): UnconfirmedSaveLog {
  const bySection = new Map<string, string[]>()

  return {
    note(sectionId, content) {
      // What saveSegment puts on the wire, so it compares with what the server stored.
      const wire = stripDiffMarkup(content)
      const texts = (bySection.get(sectionId) ?? []).filter((text) => text !== wire)
      texts.push(wire)
      bySection.set(sectionId, texts.slice(-MAX_UNCONFIRMED_PER_SECTION))
    },
    confirm(sectionId) {
      bySection.delete(sectionId)
    },
    ownWriteVersion(sectionId, error, sentVersion) {
      if (!(error instanceof ApiRequestError) || error.statusCode !== 409) return null
      const details = (error.details ?? {}) as Partial<SegmentConflictDetails>
      const { currentVersion, currentContent } = details
      if (typeof currentVersion !== 'number' || typeof currentContent !== 'string') return null
      if (currentVersion <= sentVersion) return null
      return bySection.get(sectionId)?.includes(currentContent) ? currentVersion : null
    },
  }
}

/**
 * Send a segment save through the log. If the server refuses it because it
 * already holds a text this editor sent and never saw confirmed, re-send it
 * once against the server's version. A second refusal is thrown as it came —
 * the editor shows its conflict dialog for it.
 */
export async function saveAdoptingOwnWrite(
  log: UnconfirmedSaveLog,
  sectionId: string,
  content: string,
  expectedVersion: number,
  send: (expectedVersion: number) => Promise<SaveSegmentResponse>,
): Promise<SaveSegmentResponse> {
  log.note(sectionId, content)
  let result: SaveSegmentResponse
  try {
    result = await send(expectedVersion)
  } catch (error) {
    const adoptedVersion = log.ownWriteVersion(sectionId, error, expectedVersion)
    if (adoptedVersion === null) throw error
    result = await send(adoptedVersion)
  }
  log.confirm(sectionId)
  return result
}
